import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Truck, Palette, Search, Warehouse, Star, Video, Check, ArrowRight, Sparkles } from 'lucide-react';
import CTA from '../components/CTA';

const ServicesPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const services = [
    {
      id: 'dropshipping',
      icon: Truck,
      title: 'Dropshipping Fulfillment',
      shortDesc: 'End-to-end order processing without inventory management',
      image: 'https://images.unsplash.com/photo-1768796373577-2e6e51351165?q=80&w=2000',
      description: [
        "Our dropshipping fulfillment service is designed for sellers who want to run a lean, efficient operation without the hassle of managing inventory. We handle every step of the process — from receiving your products at our warehouse to picking, packing, and shipping them directly to your customers worldwide.",
        "With real-time order syncing across all major platforms, you'll never have to manually process an order again. Our system automatically pulls orders from your Shopify, WooCommerce, Etsy, or TikTok Shop store, matches them with available inventory, and queues them for immediate processing.",
        "Whether you're shipping 10 orders a day or 10,000, our infrastructure scales with your business. Our quality assurance team inspects every item before it leaves our facility, ensuring your customers receive exactly what they ordered."
      ],
      features: [
        'Automated order syncing with 50+ ecommerce platforms',
        '24-48 hour processing times for standard orders',
        'Real-time tracking updates synced to your store',
        'Custom packaging and branded inserts available',
        'Multiple shipping carriers (USPS, UPS, FedEx, DHL, ePacket)',
        'Returns management and customer service support'
      ]
    },
    {
      id: 'print-on-demand',
      icon: Palette,
      title: 'Print on Demand',
      shortDesc: 'Custom products printed and shipped as orders come in',
      image: 'https://images.unsplash.com/photo-1503694978374-8a2fa686963a?q=80&w=2000',
      description: [
        "Turn your creative designs into real products without any upfront investment. Our print-on-demand service lets you offer custom t-shirts, hoodies, mugs, phone cases, posters, tote bags, and dozens of other products — all printed and shipped on demand as orders come in.",
        "We use state-of-the-art DTG (Direct to Garment) and sublimation printing technology to ensure vibrant, long-lasting prints. Our equipment handles everything from small text to full all-over prints with photographic quality, and we offer white-label shipping so your customers only see your brand.",
        "With zero inventory risk, you can test unlimited designs and products. Upload your artwork through our design portal, set your prices, and we handle production, quality control, and shipping. It's the easiest way to start or scale a custom merchandise business."
      ],
      features: [
        '200+ product types: apparel, drinkware, accessories, home decor',
        'DTG, sublimation, and screen printing options',
        'All-over print capability for unique designs',
        'Design mockup generator included free',
        'Sample orders at discounted prices',
        'White-label shipping with your brand'
      ]
    },
    {
      id: 'product-sourcing',
      icon: Search,
      title: 'Product Sourcing',
      shortDesc: 'Direct factory connections and competitive pricing',
      image: 'https://images.unsplash.com/photo-1695603414685-af28aff0f9d2?q=80&w=2000',
      description: [
        "Finding reliable suppliers and negotiating competitive prices is one of the biggest challenges in ecommerce. Our product sourcing team has deep relationships with thousands of verified manufacturers across China, spanning platforms like 1688, Alibaba, and direct factory networks that most Western sellers can't access.",
        "We don't just find products — we vet every supplier for quality, reliability, and ethical manufacturing practices. Our on-the-ground team visits factories, inspects production lines, and negotiates pricing that's typically 20-40% lower than what you'd find on your own. We also handle sample procurement so you can evaluate products before committing to bulk orders.",
        "Whether you need a specific product replicated, a trending item sourced quickly, or private-label goods manufactured to your specifications, our sourcing experts make it happen efficiently and affordably."
      ],
      features: [
        'Direct factory connections across 1688, Alibaba, and private networks',
        'On-site factory audits and quality verification',
        'Price negotiation saving you 20-40% on average',
        'Sample procurement and evaluation support',
        'Private label and OEM manufacturing coordination',
        'Compliance and certification assistance (CE, FDA, etc.)'
      ]
    },
    {
      id: 'global-warehousing',
      icon: Warehouse,
      title: 'Global Warehousing',
      shortDesc: 'Strategic warehouse locations worldwide for faster delivery',
      image: 'https://images.unsplash.com/photo-1718289518008-2a6e78a87488?q=80&w=2000',
      description: [
        "Speed of delivery is one of the most important factors in customer satisfaction and repeat purchases. Our global warehousing network puts your products closer to your customers, dramatically reducing shipping times and costs. With facilities in China (Shenzhen and Yiwu), the United States (Los Angeles and New Jersey), Europe (UK and Germany), and Australia (Melbourne), we've got every major market covered.",
        "Our warehouse management system provides real-time inventory visibility across all locations, automatic reorder alerts, and intelligent routing that selects the optimal warehouse for each order based on the customer's location. This means faster delivery, lower shipping costs, and happier customers.",
        "We offer flexible storage options from as few as 10 units to entire pallets, with competitive monthly rates and no long-term contracts. Our climate-controlled facilities are monitored around the clock with advanced security systems to protect your inventory."
      ],
      features: [
        '5 warehouse locations: China, US, UK, Germany, Australia',
        'Real-time inventory tracking across all facilities',
        'Intelligent order routing for optimal shipping',
        'Automatic low-stock alerts and reorder management',
        'Climate-controlled, secured facilities',
        'Flexible storage — no long-term contracts required'
      ]
    },
    {
      id: 'custom-branding',
      icon: Star,
      title: 'Custom Branding',
      shortDesc: 'Create memorable unboxing experiences',
      image: 'https://images.unsplash.com/photo-1716193348750-9e22b67718c6?q=80&w=2000',
      description: [
        "In today's competitive ecommerce landscape, brand experience doesn't end at checkout — it extends all the way to unboxing. Our custom branding service helps you create memorable unboxing experiences that drive customer loyalty, social media shares, and repeat purchases.",
        "We offer comprehensive branding options including custom printed boxes, branded tissue paper, personalized thank-you cards, promotional inserts, custom stickers, and branded packing tape. Our design team can help you create cohesive packaging that perfectly represents your brand identity, or we can work with your existing designs.",
        "Whether you're a startup looking for simple branded touches or an established brand that wants full custom packaging, we offer solutions at every price point. Our minimum order quantities are flexible, and we store your branded materials in our warehouses for seamless integration with every shipment."
      ],
      features: [
        'Custom printed mailer boxes and poly mailers',
        'Branded tissue paper, stickers, and packing tape',
        'Personalized thank-you cards and promotional inserts',
        'Gift wrapping and premium packaging options',
        'In-house design team for branding assistance',
        'Low MOQ — start from just 100 pieces'
      ]
    },
    {
      id: 'tiktok-shop',
      icon: Video,
      title: 'TikTok Shop Solutions',
      shortDesc: 'Full TikTok Shop fulfillment with SCAN label support',
      image: 'https://images.unsplash.com/photo-1763872011479-aa293bf083a8?q=80&w=2000',
      description: [
        "TikTok Shop is the fastest-growing ecommerce channel, and selling through it requires a fulfillment partner who understands its unique requirements. Juniperstandard is fully equipped to handle TikTok Shop fulfillment, including the critical SCAN label system that TikTok requires for all shipments within the platform.",
        "When a product goes viral on TikTok, order volume can spike from 10 orders a day to 10,000 overnight. Our scalable infrastructure is built to handle exactly these kinds of demand surges. We maintain buffer stock for your best sellers and can rapidly scale our workforce to meet unexpected spikes without compromising on quality or speed.",
        "We also provide strategic support for TikTok sellers, including trending product recommendations, optimal pricing strategies, and fulfillment configurations that maximize your seller rating. Our team stays current with TikTok Shop's ever-evolving policies to ensure your account stays compliant."
      ],
      features: [
        'Full SCAN label compliance for TikTok Shop',
        'Surge capacity for viral product handling',
        'Same-day processing for TikTok orders',
        'Trending product sourcing and recommendations',
        'TikTok Shop policy compliance monitoring',
        'US warehouse for fast domestic shipping'
      ]
    }
  ];

  return (
    <div>
      {/* Elegant Hero Section - Green Theme */}
      <div className="relative bg-gradient-to-br from-emerald-50 via-teal-50 to-white py-40 mt-20 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="services-wave" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <path d="M0 50 Q 25 30, 50 50 T 100 50" stroke="#10B981" strokeWidth="1" fill="none" opacity="0.3"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#services-wave)" />
          </svg>
        </div>
        <div className="absolute top-10 left-10 w-72 h-72 bg-emerald-300 rounded-full opacity-10 blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-teal-300 rounded-full opacity-10 blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-white border-2 border-emerald-200 rounded-full px-6 py-3 mb-8 shadow-lg">
            <Sparkles size={20} className="text-emerald-600" />
            <span className="text-emerald-700 font-bold text-sm uppercase tracking-wider">Our Complete Solutions</span>
          </div>
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-black text-gray-900 mb-6 leading-none">
            <span className="block">Fulfillment</span>
            <span className="block bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Solutions</span>
          </h1>
          <p className="text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            Complete fulfillment solutions — from sourcing to shipping, we provide everything your ecommerce business needs to operate smoothly and scale effortlessly.
          </p>
        </div>
      </div>

      {/* Services Detail Section - Modern Layout */}
      <section className="py-32 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-40">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={service.id}
                  id={service.id}
                  className="scroll-mt-24 relative"
                >
                  <div className={`flex flex-col ${
                    isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  } items-center gap-16`}>
                    
                    {/* Content Side */}
                    <div className="flex-1">
                      {/* Icon Badge */}
                      <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center mb-8 shadow-lg">
                        <IconComponent size={32} className="text-white" />
                      </div>

                      {/* Title */}
                      <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-4 leading-tight">
                        {service.title}
                      </h2>
                      
                      {/* Short Description */}
                      <p className="text-xl text-emerald-600 font-bold mb-8">
                        {service.shortDesc}
                      </p>
                      
                      {/* Description Paragraphs */}
                      <div className="space-y-6 mb-8">
                        {service.description.map((paragraph, idx) => (
                          <p key={idx} className="text-lg text-gray-700 leading-relaxed">
                            {paragraph}
                          </p>
                        ))}
                      </div>
                      
                      {/* Features List */}
                      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border-2 border-emerald-200 mb-8">
                        <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center space-x-2">
                          <Check size={28} className="text-emerald-600" />
                          <span>Key Features</span>
                        </h3>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {service.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start space-x-3">
                              <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center mt-0.5">
                                <Check size={16} className="text-white" />
                              </div>
                              <span className="text-gray-700 font-medium">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* CTA Button */}
                      <Link
                        to="/contact"
                        className="inline-flex items-center space-x-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-10 py-5 rounded-xl font-black text-xl hover:shadow-2xl hover:shadow-emerald-500/50 transition-all duration-300"
                      >
                        <span>Get Started</span>
                        <ArrowRight size={24} />
                      </Link>
                    </div>

                    {/* Image Side */}
                    <div className="flex-1">
                      <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                        <div 
                          className="h-[500px] bg-cover bg-center"
                          style={{
                            backgroundImage: `url(${service.image})`
                          }}
                        >
                          {/* Gradient Overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/50 to-transparent"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Not Sure Section */}
      <section className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-12 shadow-xl border-2 border-emerald-200 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl mb-8">
              <Sparkles size={32} className="text-emerald-600" />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              <span className="block">Not Sure Which</span>
              <span className="block bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Service You Need?</span>
            </h2>
            <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed">
              Our fulfillment experts will analyze your business and recommend the perfect combination of services. Free consultation, no commitment.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-10 py-5 rounded-xl font-black text-xl hover:shadow-2xl hover:shadow-emerald-500/50 transition-all duration-300"
            >
              <span>Talk to an Expert</span>
              <ArrowRight size={24} />
            </Link>
          </div>
        </div>
      </section>

      <CTA />
    </div>
  );
};

export default ServicesPage;
